import { useMemo, useRef } from 'react'

import {
  Box,
  Button,
  Checkbox,
  Form,
  Grid,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import {
  EntitySelect,
  EntitySelectEntity,
  NumberInput,
  RichTextEditor,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsContractTypesEnum,
  AtsGetContractTypesDocument,
  AtsGetJobPostingSettingsDescriptionDocument,
  AtsJobPostingSalaryFormatEnum,
  AtsJobPostingSalaryPeriodEnum,
  AtsJobPostingSettingsDescriptionFragment,
  AtsUpdateJobPostingDescriptionDocument,
  ContractTypeFragment,
  OnboardingEntitiesWorkScheduleTimeEnum,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobCatalogLinkedInfoAlert } from 'modules/ats/pages/JobPostingSettings/JobCatalogAlerts/LinkedInfoAlert'
import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { DescriptionSkeleton } from './DescriptionSkeleton'
import { GenerateDraftDescription, useGenerateDraftDescription } from './GenerateDraftDescription'
import { GenerateDraftLoading } from './GenerateDraftDescription/GenerateDraftLoading'

interface UpdateJobPostingDescriptionForm {
  contractType: AtsContractTypesEnum | null
  description: string
  hideSalary: boolean
  salaryCurrency: string | null
  salaryFormat: AtsJobPostingSalaryFormatEnum | null
  salaryFromAmountInCents: number
  salaryPeriod: AtsJobPostingSalaryPeriodEnum
  salaryToAmountInCents: number
  scheduleType: OnboardingEntitiesWorkScheduleTimeEnum | null
}

interface Props {
  jobId: string
}

export const Description = ({ jobId }: Props) => {
  const { company } = useCurrentSession()
  const { canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const { data, loading } = useQuery(AtsGetJobPostingSettingsDescriptionDocument, {
    variables: {
      id: jobId,
      companyId: company.id,
    },
  })

  const { data: contractTypesData, loading: loadingContractTypes } = useQuery(
    AtsGetContractTypesDocument
  )

  const isLoading = loading || loadingContractTypes || richTextEditorLoading

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessDescription)
  }, [])

  const job = data?.ats.jobPosting
  const enabledFeatures = data?.enabledFeatures.featuresConnection.nodes ?? []
  const currencies = data?.apiCore?.currenciesConnection?.edges ?? []

  const jobCatalogFeatureEnabled =
    enabledFeatures.find((feature) => feature.name === 'dev_ats_job_posting_revamp')?.enabled ??
    false

  if (isLoading || !job) {
    return <DescriptionSkeleton />
  }

  return (
    <DescriptionForm
      job={job}
      contractTypes={contractTypesData?.ats.contractTypesConnection.nodes || []}
      currencies={currencies}
      canUseRichTextEditor={canUseRichTextEditor}
      jobCatalogSerializedTree={data?.jobCatalog.treesConnection.nodes[0]?.serializedTree ?? ''}
      jobCatalogFeatureEnabled={jobCatalogFeatureEnabled}
    />
  )
}

interface DescriptionFormProps {
  job: AtsJobPostingSettingsDescriptionFragment
  contractTypes: ContractTypeFragment[]
  currencies: ReadonlyArray<{ node: { isoCode: string; priority: number | null } }>
  canUseRichTextEditor: boolean
  jobCatalogSerializedTree: string
  jobCatalogFeatureEnabled: boolean
}

const DescriptionForm = ({
  job,
  canUseRichTextEditor,
  contractTypes,
  currencies,
  jobCatalogSerializedTree,
  jobCatalogFeatureEnabled,
}: DescriptionFormProps) => {
  const { has_ats_ai_job_posting_description } = useFeatures(['ats_ai_job_posting_description'])
  const { editorConfig } = useRichTextEditor()

  const textEditorRef = useRef<EditorHandle>(null)
  const [showHideSalaryTooltip, toggleHideSalaryTooltip] = useToggle(false)
  const [updateJobPosting] = useMutation(AtsUpdateJobPostingDescriptionDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const {
    generateDraftDescription,
    generatingDraftDescription,
    alreadyGeneratedDraftDescription,
    draftDescription,
  } = useGenerateDraftDescription(job.id)

  const options: Array<{ label: string; value: AtsJobPostingSalaryPeriodEnum }> = [
    {
      label: i18n.t('ats.job_salary_period.annual'),
      value: 'annual',
    },
    {
      label: i18n.t('ats.job_salary_period.monthly'),
      value: 'monthly',
    },
    {
      label: i18n.t('ats.job_salary_period.daily'),
      value: 'daily',
    },
  ]

  const convertToCents = (value: number | undefined | null) =>
    !!value && isFinite(value) ? Math.round(value * 100) : 0

  const convertFromCents = (value: number | undefined | null) => (value ? value / 100 : 0)

  const currencyOptions = useMemo(
    () =>
      [...currencies]
        .map((c) => ({ label: c.node.isoCode, value: c.node.isoCode }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [currencies]
  )

  const form = useForm<UpdateJobPostingDescriptionForm>({
    defaultValues: {
      contractType: job.contractType ?? null,
      description: job.description ?? '',
      hideSalary: job.hideSalary ?? false,
      salaryCurrency: job.salaryCurrency ?? job.legalEntity?.currency ?? null,
      salaryFormat: job.salaryFormat ?? null,
      salaryFromAmountInCents: convertFromCents(job.salaryFromAmountInCents),
      salaryPeriod: job.salaryPeriod ?? 'annual',
      salaryToAmountInCents: convertFromCents(job.salaryToAmountInCents),
      scheduleType: job.scheduleType ?? null,
    },
    async onSubmit(values, { setErrors }) {
      track(trackingEvents.ats.jobSettingsDescriptionSubmit)

      const result = await updateJobPosting({
        variables: {
          id: job.id,
          contractType: values.contractType || null,
          description: values.description,
          hideSalary: values.hideSalary,
          salaryCurrency: values.salaryCurrency,
          salaryFormat: values.salaryFormat || null,
          salaryFromAmountInCents: convertToCents(values.salaryFromAmountInCents),
          salaryPeriod: values.salaryPeriod,
          salaryToAmountInCents: convertToCents(values.salaryToAmountInCents),
          scheduleType: values.scheduleType || null,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
    validate(values) {
      const errors: FormErrors<UpdateJobPostingDescriptionForm> = {}

      if (!options.some((option) => option.value === values.salaryPeriod)) {
        errors.salaryPeriod = i18n.t('common.validations.empty')
      }

      return errors
    },
  })

  const salaryFromAmountLabel =
    form.values.salaryFormat === 'range'
      ? i18n.t('ats.job_posting_settings.salary.salary_from_amount_range')
      : i18n.t('ats.job_posting_settings.salary.salary_from_amount')

  const formattedContractTypes = contractTypes.map(({ id, label, country }) => ({
    label,
    country,
    value: id,
  }))

  const showLinkedInformationAlert =
    jobCatalogFeatureEnabled && !!(job.jobCatalogNodeRolesUuid && job.jobCatalogNodeLevelsUuid)

  const contractTypesEntities: EntitySelectEntity[] = useMemo(() => {
    const groupedFormattedContractTypes = groupBy(
      formattedContractTypes,
      (item) => item.country ?? 'all'
    )
    const entities: EntitySelectEntity[] = Object.entries(groupedFormattedContractTypes).map(
      ([countryKey, items]) => ({
        id: `group_${countryKey}`,
        name: countryKey,
        expanded: true,
        subItems: items
          .filter((i) => i.label)
          .sort((x, y) => x.label.localeCompare(y.label))
          .map(({ value, label, country: countryCode }) => ({
            subId: value,
            subName: label,
            subSearchKeys: [label, value, countryCode ?? ''],
          })),
      })
    )
    return entities
  }, [formattedContractTypes])

  const selectedEntities: EntitySelectEntity[] = useMemo(() => {
    const selectedId = form.values.contractType
    if (!selectedId) return []

    for (const group of contractTypesEntities) {
      const sub = group.subItems?.find((s) => s.subId === selectedId)
      if (sub) return [{ ...group, subItems: [sub] }]
    }

    return []
  }, [contractTypesEntities, form.values.contractType])

  return (
    <SectionComponentLayout
      sidebar={
        showLinkedInformationAlert && (
          <JobCatalogLinkedInfoAlert
            jobCatalogSerializedTree={jobCatalogSerializedTree}
            jobCatalogLevelUuid={job.jobCatalogNodeLevelsUuid ?? ''}
            jobCatalogRoleUuid={job.jobCatalogNodeRolesUuid ?? ''}
          />
        )
      }
    >
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <Box gap="s40" paddingBottom="s16">
          <Box gap="s16">
            <Text size="400" color="grey1000" weight="semibold">
              {i18n.t('ats.job_posting_settings.details.title')}
            </Text>
            <Grid variant="fixed">
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <EntitySelect
                  label={i18n.t('ats.job_posting_settings.details.contract_type_label')}
                  notFoundTitle={i18n.t(
                    'ats.job_posting_settings.description.contract_type.not_found'
                  )}
                  notFoundSubtitle={''}
                  placeholder={i18n.t('common.select_basic')}
                  onSelect={(entity) => {
                    if (!entity?.subItems?.[0]) {
                      return
                    }
                    const id = entity.subItems[0].subId
                    if (id) {
                      form.setFieldValue('contractType', id)
                    }
                  }}
                  entities={contractTypesEntities}
                  groups={[]}
                  selectedGroup="all"
                  onItemExpandedChange={(_id, _expanded) => {}}
                  onGroupChange={(_value) => {}}
                  selectedEntities={selectedEntities}
                  selectedItemsCopy={i18n.t(
                    'job_catalog.job_catalog_tree_selector.role.trigger_selected'
                  )}
                  hiddenAvatar
                  singleSelector
                />
              </Box>
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <F0Select
                  label={i18n.t('ats.job_posting_settings.details.schedule_type_label')}
                  placeholder={i18n.t('common.select_basic')}
                  value={form.values.scheduleType ?? undefined}
                  onChange={(value: OnboardingEntitiesWorkScheduleTimeEnum) =>
                    form.setFieldValue('scheduleType', value)
                  }
                  options={[
                    {
                      label: i18n.t('ats.job_schedule.full_time'),
                      value: 'full_time',
                    },
                    {
                      label: i18n.t('ats.job_schedule.part_time'),
                      value: 'part_time',
                    },
                  ]}
                  size="md"
                />
              </Box>
            </Grid>
          </Box>
          {job.salaryPermission && (
            <Box gap="s16">
              <Text size="400" color="grey1000" weight="semibold">
                {i18n.t('ats.job_posting_settings.salary.title')}
              </Text>
              <Grid variant="fixed">
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.job_posting_settings.salary.salary_period_label')}
                    placeholder={i18n.t('common.select_basic')}
                    value={form.values.salaryPeriod}
                    onChange={(salaryPeriod: AtsJobPostingSalaryPeriodEnum) =>
                      form.setFieldValue('salaryPeriod', salaryPeriod)
                    }
                    options={options}
                    error={form.errors.salaryPeriod}
                    size="md"
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.job_posting_settings.salary.salary_format_label')}
                    placeholder={i18n.t('common.select_basic')}
                    value={form.values.salaryFormat ?? undefined}
                    onChange={(salaryFormat: AtsJobPostingSalaryFormatEnum) =>
                      form.setFieldValue('salaryFormat', salaryFormat)
                    }
                    options={[
                      {
                        label: i18n.t('ats.job_salary_format.range'),
                        value: 'range',
                      },
                      {
                        label: i18n.t('ats.job_salary_format.fixed_amount'),
                        value: 'fixed_amount',
                      },
                    ]}
                    size="md"
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <F0Select
                    label={i18n.t('ats.job_posting_settings.salary.currency_label')}
                    name="salaryCurrency"
                    placeholder={i18n.t('common.select')}
                    value={form.values.salaryCurrency ?? undefined}
                    onChange={(value: string | undefined) =>
                      form.setFieldValue('salaryCurrency', value ?? null)
                    }
                    showSearchBox
                    searchEmptyMessage={i18n.t('ats.vacancies.creation.search_no_results_found')}
                    options={currencyOptions}
                    size="md"
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }} />
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  <NumberInput
                    label={salaryFromAmountLabel}
                    locale={i18n.locale}
                    placeholder={i18n.t('common.select_basic')}
                    value={form.values.salaryFromAmountInCents}
                    onChange={(salaryFromAmountInCents) =>
                      form.setFieldValue('salaryFromAmountInCents', Number(salaryFromAmountInCents))
                    }
                    units={form.values.salaryCurrency ?? ''}
                    size="md"
                  />
                </Box>
                <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                  {form.values.salaryFormat === 'range' && (
                    <NumberInput
                      label={i18n.t('ats.job_posting_settings.salary.salary_to_amount')}
                      locale={i18n.locale}
                      value={form.values.salaryToAmountInCents}
                      onChange={(salaryToAmountInCents) =>
                        form.setFieldValue('salaryToAmountInCents', Number(salaryToAmountInCents))
                      }
                      units={form.values.salaryCurrency ?? ''}
                      error={form.errors.salaryToAmountInCents}
                      size="md"
                    />
                  )}
                </Box>
              </Grid>
              <Box flexDirection="row" gap="s8" alignItems="center" paddingTop="s8">
                <Checkbox
                  checked={form.values.hideSalary}
                  label={i18n.t('ats.job_posting_settings.salary.hide_salary_label')}
                  onChange={(event) => form.setFieldValue('hideSalary', event.target.checked)}
                />
                <Tooltip.Alt
                  attachment="middle left"
                  open={showHideSalaryTooltip}
                  toggle={toggleHideSalaryTooltip}
                  label={i18n.t('ats.job_posting_settings.salary.hide_salary_tooltip')}
                >
                  <Icon.Medium icon="info-circle-line" color="grey800" />
                </Tooltip.Alt>
              </Box>
            </Box>
          )}

          <Box gap="s16">
            <Text size="400" color="grey1000" weight="semibold">
              {i18n.t('ats.job_posting_settings.description.title')}
            </Text>
            {has_ats_ai_job_posting_description && (
              <GenerateDraftDescription
                onClickGenerate={generateDraftDescription}
                loading={generatingDraftDescription}
                alreadyGenerated={alreadyGeneratedDraftDescription}
                currentDescription={form.values.description}
              />
            )}

            {generatingDraftDescription ? (
              <GenerateDraftLoading />
            ) : canUseRichTextEditor ? (
              <RichTextEditor
                {...editorConfig}
                title={i18n.t('ats.job_posting_settings.description.title')}
                onChange={(data) => form.setFieldValue('description', data?.value)}
                placeholder={i18n.t('ats.job_posting_settings.description.placeholder')}
                initialEditorState={{
                  content: draftDescription ?? job.description ?? '',
                }}
                maxCharacters={10000}
              />
            ) : (
              <TextEditor
                ref={textEditorRef}
                onChange={(description) => form.setFieldValue('description', description)}
                placeholder={i18n.t('ats.job_posting_settings.description.placeholder')}
                initialEditorState={{
                  data: draftDescription ?? form.values.description,
                }}
                height="s600"
                enableBetaFeatures={false}
              />
            )}
          </Box>
        </Box>
        <Button disabled={form.isSubmitting || !form.isValid} submit>
          {i18n.t('common.submit')}
        </Button>
      </Form>
    </SectionComponentLayout>
  )
}
